package org.wordsapp.ui.screen

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ContentPaste
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import org.wordsapp.model.AuthState
import org.wordsapp.readClipboard
import org.wordsapp.ui.component.PrimaryButton
import org.wordsapp.viewmodel.WordViewModel

@Composable
fun SettingsScreen(viewModel: WordViewModel) {
    val authState by viewModel.authState.collectAsState()
    var spreadsheetUrl by remember { mutableStateOf("") }
    val scope = rememberCoroutineScope()

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(16.dp),
    ) {
        Text(
            text = "Settings",
            style = MaterialTheme.typography.headlineMedium,
            fontWeight = FontWeight.Bold,
        )

        Spacer(Modifier.height(24.dp))

        // Account section
        Card(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(12.dp),
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                Text(
                    "Account",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                )
                Spacer(Modifier.height(8.dp))
                when (val state = authState) {
                    is AuthState.Authenticated -> {
                        Text("Signed in as: ${state.email ?: "Unknown"}")
                        Spacer(Modifier.height(8.dp))
                        OutlinedButton(onClick = { viewModel.logout() }) {
                            Text("Sign Out")
                        }
                    }
                    is AuthState.Demo -> {
                        Text("Using Demo Mode")
                    }
                    else -> {
                        Text("Not signed in")
                    }
                }
            }
        }

        Spacer(Modifier.height(16.dp))

        // Spreadsheet section
        Card(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(12.dp),
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                Text(
                    "Google Spreadsheet",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                )
                Spacer(Modifier.height(8.dp))
                Text(
                    "Paste the URL of your Google Sheets spreadsheet. " +
                        "It should have columns: A (foreign word), B (translation), C (stats).",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Spacer(Modifier.height(12.dp))
                OutlinedTextField(
                    value = spreadsheetUrl,
                    onValueChange = { spreadsheetUrl = it },
                    modifier = Modifier.fillMaxWidth(),
                    label = { Text("Spreadsheet URL") },
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    trailingIcon = {
                        IconButton(onClick = {
                            scope.launch {
                                readClipboard()?.let { spreadsheetUrl = it }
                            }
                        }) {
                            Icon(Icons.Default.ContentPaste, contentDescription = "Paste")
                        }
                    },
                )
                Spacer(Modifier.height(12.dp))
                PrimaryButton(
                    text = "Save & Load Words",
                    onClick = { viewModel.setSpreadsheetUrl(spreadsheetUrl) },
                    enabled = spreadsheetUrl.isNotBlank(),
                )
            }
        }

        Spacer(Modifier.height(16.dp))

        // Practice section
        val ignoreDiacritics by viewModel.ignoreDiacritics.collectAsState()
        Card(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(12.dp),
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                Text(
                    "Practice",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                )
                Spacer(Modifier.height(8.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text("Ignore diacritics")
                        Text(
                            "Treat o/\u00f6, u/\u00fc, a/\u00e4, ss/\u00df as equal",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                    Switch(
                        checked = ignoreDiacritics,
                        onCheckedChange = { viewModel.setIgnoreDiacritics(it) },
                    )
                }
            }
        }

        Spacer(Modifier.height(16.dp))

        // Instructions card
        Card(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(12.dp),
            colors = CardDefaults.cardColors(
                containerColor = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.3f)
            ),
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                Text(
                    "How to set up",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                )
                Spacer(Modifier.height(8.dp))
                Text("1. Create a Google Sheets spreadsheet")
                Text("2. Add words: column A = foreign word, B = translation")
                Text("3. Share the spreadsheet with your Google account")
                Text("4. Paste the URL above and click Save")
            }
        }
    }
}
